import { createRouter, createWebHistory } from 'vue-router';

// All views are lazy-loaded (`() => import(...)`) so each route is code-split
// into its own on-demand chunk. This keeps the initial entry bundle small —
// a visitor landing on the Splash page no longer downloads every other view.

const routes = [
  {
    path: '/',
    name: 'Splash',
    component: () => import('../views/Splash.vue'),
    meta: { title: 'Welcome' }
  },
  {
    path: '/signup',
    name: 'Signup',
    component: () => import('../views/Signup.vue'),
    meta: { title: 'Sign Up' }
  },
  {
    path: '/home',
    name: 'Home',
    component: () => import('../views/Home.vue'),
    meta: { title: 'Home' }
  },
  {
    path: '/forum',
    name: 'Categories',
    component: () => import('../views/Categories.vue'),
    meta: { title: 'Forum' }
  },
  {
    path: '/category/:categorySlug',
    name: 'CategoryThreads',
    component: () => import('../views/CategoryThreads.vue'),
    props: true,
    meta: { title: 'Forum Category' }
  },
  {
    path: '/category/:categorySlug/new',
    name: 'CreateThread',
    component: () => import('../views/CreateThread.vue'),
    props: true,
    meta: { title: 'New Thread' }
  },
  {
    path: '/thread/:categorySlug/:threadSlug',
    name: 'ThreadView',
    component: () => import('../views/ThreadView.vue'),
    props: true,
    meta: { title: 'Thread' }
  },
  {
    path: '/account/settings',
    name: 'AccountSettings',
    component: () => import('../views/AccountSettings.vue'),
    meta: { title: 'Account Settings' }
  },
  {
    path: '/recent',
    name: 'RecentActivity',
    component: () => import('../views/RecentActivity.vue'),
    meta: { title: 'Recent Activity' }
  },
  {
    path: '/forum/search',
    name: 'SearchResults',
    component: () => import('../views/SearchResultsView.vue'),
    meta: { title: 'Search Results' }
  },
  {
    path: '/account',
    name: 'Account',
    component: () => import('../views/AccountView.vue'),
    meta: { title: 'Account' }
  },
  {
    path: '/account/posts',
    name: 'MyPosts',
    component: () => import('../views/MyPostsView.vue'),
    meta: { title: 'My Posts' }
  },
  {
    path: '/profile/:username',
    name: 'Profile',
    component: () => import('../views/ProfileView.vue'),
    props: true,
    meta: { title: 'User Profile' }
  },
  {
    path: '/games',
    name: 'Games',
    component: () => import('../views/Games.vue'),
    meta: { title: 'Games' }
  },
  {
    path: '/creativity',
    name: 'Creativity',
    component: () => import('../views/Creativity.vue'),
    meta: { title: 'Creativity' }
  },
  {
    path: '/games/cafe-pop',
    name: 'CafePop',
    component: () => import('../views/popperGame.vue'),
    meta: { title: 'Café Pop!' }
  },
  {
    path: '/shops',
    name: 'ShopsOverview',
    component: () => import('../views/ShopsOverview.vue'),
    meta: { title: 'Shops' }
  },
  {
    // Bare /shop keeps working and resolves to the default shop (#25).
    path: '/shop',
    name: 'Shop',
    component: () => import('../views/Shop.vue'),
    meta: { title: 'Shop' }
  },
  {
    path: '/shop/:shopSlug',
    name: 'ShopBySlug',
    component: () => import('../views/Shop.vue'),
    meta: { title: 'Shop' }
  },
  {
    path: '/wardrobe',
    name: 'Wardrobe',
    component: () => import('../views/Wardrobe.vue'),
    meta: { title: 'Wardrobe' }
  },
  {
    path: '/design-studio',
    name: 'DesignStudio',
    component: () => import('../views/DesignStudio.vue'),
    meta: { title: 'Design Studio' }
  },
  {
    path: '/design-studio/design/:id',
    name: 'DesignDetail',
    component: () => import('../views/DesignDetailPage.vue'),
    meta: { title: 'Design Detail' }
  },
  {
    path: '/sprite-builder',
    name: 'SpriteBuilder',
    component: () => import('../views/SpriteBuilderPage.vue'),
    meta: { title: 'Sprite Sheet Builder' }
  },
  {
    path: '/design-review',
    name: 'DesignReview',
    component: () => import('../views/DesignReview.vue'),
    meta: { title: 'Design Review', requiresAuth: true }
  },
  {
    path: '/moderation',
    name: 'ModerationDashboard',
    component: () => import('../views/ModerationDashboard.vue'),
    meta: { title: 'Moderation', requiresAuth: true }
  },
  {
    path: '/moderation/report/:reportId',
    name: 'ModerationReportDetail',
    component: () => import('../views/ModerationReportDetail.vue'),
    props: true,
    meta: { title: 'Report Review', requiresAuth: true }
  },
  {
    path: '/account/moderation',
    name: 'AccountModeration',
    component: () => import('../views/AccountModeration.vue'),
    meta: { title: 'Moderation History', requiresAuth: true }
  },
  {
    path: '/appeal/:appealId',
    name: 'AppealDetail',
    component: () => import('../views/AppealDetail.vue'),
    props: true,
    meta: { title: 'Appeal', requiresAuth: true }
  },
  {
    path: '/competitions',
    name: 'ContestList',
    component: () => import('../views/ContestList.vue'),
    meta: { title: 'Competitions' }
  },
  {
    path: '/competitions/create',
    name: 'ContestCreate',
    component: () => import('../views/ContestCreate.vue'),
    meta: { title: 'Create Competition', requiresAuth: true }
  },
  {
    path: '/competitions/:slug',
    name: 'ContestDetail',
    component: () => import('../views/ContestDetail.vue'),
    props: true,
    meta: { title: 'Competition' }
  },
  {
    path: '/competitions/:slug/submit',
    name: 'ContestSubmit',
    component: () => import('../views/ContestSubmit.vue'),
    props: true,
    meta: { title: 'Submit Entry', requiresAuth: true }
  },
  {
    path: '/impressum',
    name: 'Impressum',
    component: () => import('../views/Impressum.vue'),
    meta: { title: 'Impressum' }
  },
  {
    path: '/copyright',
    name: 'CopyrightNotice',
    component: () => import('../views/CopyrightNotice.vue'),
    meta: { title: 'Copyright Notice' }
  },
  {
    path: '/terms',
    name: 'TermsOfService',
    component: () => import('../views/TermsOfService.vue'),
    meta: { title: 'Terms of Service' }
  },
  {
    path: '/cookies',
    name: 'CookiePolicy',
    component: () => import('../views/CookiePolicy.vue'),
    meta: { title: 'Cookie Policy' }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: () => import('../views/PrivacyPolicy.vue'),
    meta: { title: 'Privacy Policy' }
  },
  {
    path: '/rules',
    name: 'RulesOfConduct',
    component: () => import('../views/RulesOfConduct.vue'),
    meta: { title: 'Rules of Conduct' }
  },
  {
    path: '/attributions',
    name: 'Attributions',
    component: () => import('../views/Attributions.vue'),
    meta: { title: 'Attributions' }
  },
  {
    path: '/planner-page',
    name: 'PlannerPage',
    component: () => import('../views/PlannerPage.vue'),
    meta: { title: 'Planner' }
  },
  {
    path: '/messages',
    name: 'Messages',
    component: () => import('../views/MessagesView.vue'),
    meta: { title: 'Messages', requiresAuth: true }
  },
  {
    path: '/messages/:username',
    name: 'MessagesWith',
    component: () => import('../views/MessagesView.vue'),
    props: true,
    meta: { title: 'Messages', requiresAuth: true }
  },
  {
    path: '/artist-page',
    name: 'ArtistPage',
    component: () => import('../views/ArtistPage.vue'),
    meta: { title: 'Artist' }
  },
  {
    path: '/reporter-page',
    name: 'ReporterPage',
    component: () => import('../views/ReporterPage.vue'),
    meta: { title: 'Reporter' }
  },
  {
    path: '/studio/sandbox',
    name: 'StudioSandbox',
    component: () => import('../views/StudioSandbox.vue'),
    meta: { title: 'Artist Sandbox', requiresAuth: true }
  },
  {
    path: '/admin/audit',
    name: 'AdminAudit',
    component: () => import('../views/AdminAudit.vue'),
    meta: { title: 'Audit Log', requiresAuth: true }
  },
  {
    path: '/admin/workflows',
    name: 'AdminWorkflows',
    component: () => import('../views/AdminWorkflows.vue'),
    meta: { title: 'Workflow Inspection', requiresAuth: true }
  },
  {
    // Deep link to one instance. Same view — it renders the detail panel
    // for the id and falls back to the list when the id is dropped.
    path: '/admin/workflows/:instanceId',
    name: 'AdminWorkflowInstance',
    component: () => import('../views/AdminWorkflows.vue'),
    props: true,
    meta: { title: 'Workflow Inspection', requiresAuth: true }
  },
  {
    path: '/admin/users/:userId/history',
    name: 'AdminUserHistory',
    component: () => import('../views/AdminUserHistory.vue'),
    props: true,
    meta: { title: 'User Moderation History', requiresAuth: true }
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Set page titles dynamically
router.beforeEach((to, from, next) => {
  const baseTitle = 'Citopia - A forum based community with pixel art avatars';
  if (to.meta.title) {
    document.title = `Citopia - ${to.meta.title} - A forum based community with pixel art avatars`;
  } else {
    document.title = baseTitle;
  }
  next();
});

export default router;
